import React, { memo, useEffect, useRef } from "react";
import urls from "../../constants";
import { CurrencyConvertion } from "../../utils/Validate";
import { ProductSdrPopover } from "./ProductSdrPopover";
import {
  SizeFieldCell,
  InlineSelectToggleCell,
  useVariantOptions,
  isSquareDiffuser,
} from "./SizeSelectField";
import { CheckNull } from "../../utils/Validate";
import SkeletonProductQuotation from "../common/skeletonProductQuotation";
export const isVariantHidden = (variant, configuration) => {
  if (urls?.LIST_PRICE_CONSTANT.includes(variant?.variantId)) return true;
  if (urls?.NET_LIST_PRICE_CONSTANT.includes(variant?.variantId)) return true;
  if (urls?.FACE_WIDTH_ARRAY.includes(variant?.variantId)) return true;
  if (urls?.FACE_LENGTH_ARRAY.includes(variant?.variantId)) return true;
  if (urls?.FACE_HEIGHT_ARRAY.includes(variant?.variantId)) return true;
  if (urls?.FACE_DIAMETER.includes(variant?.variantId)) return true;

  const blankPlateNone = configuration?.some(
    (config) =>
      config?.attributeId === urls?.BLANK_PLATE &&
      urls?.BLANK_PLATE_NONE?.includes(config?.valueName?.trim())
  );

  return blankPlateNone && urls?.BOP_LENGTH === variant?.variantId;
};

const getVariantDisplayValue = (variant, values, currencyDetail) => {
  if (urls?.ACTUATOR_QTY_CONSTANT?.includes(variant?.variantId)) {
    const rawValue = variant.variantValue;
    const numericValue = Number(rawValue);

    if (
      rawValue === "" ||
      rawValue === null ||
      rawValue === undefined ||
      Number.isNaN(numericValue)
    ) {
      return rawValue || "";
    }

    return Number.isInteger(numericValue)
      ? String(numericValue)
      : String(numericValue);
  }

  if (!urls?.PRICE_CONSTANT?.includes(variant?.variantId)) {
    const rawVal = variant.variantValue;
    if (rawVal !== "" && rawVal !== null && rawVal !== undefined && !Number.isNaN(Number(rawVal))) {
      return Number(rawVal).toFixed(2);
    }
    return rawVal || "";
  }

  const rawValue = variant.variantValue;
  const isEmptyOrInvalid =
    rawValue === "" ||
    rawValue === null ||
    rawValue === undefined ||
    Number.isNaN(Number(rawValue));

  return isEmptyOrInvalid ? Number(0).toFixed(1) : CurrencyConvertion(rawValue, values?.conversionRate, currencyDetail);
};

const normalizeName = (name) => (name || "").toString().trim().toLowerCase();

const getVariantColumnClass = (variant) => {
  const name = normalizeName(variant?.variantName);
  if (name === "#") return "size-col-serial";
  if (name.includes("tag ref")) return "size-col-tag-ref";
  if (name.includes("other ref")) return "size-col-other-ref";
  if (name.includes("qty") || name.includes("quantity")) return "size-col-quantity";
  if (name.includes("unit") || name.includes("price") || name.includes("total")) return "size-col-price";
  if (
    name.includes("diameter") ||
    name.includes("width") ||
    name.includes("height") ||
    name.includes("length")
  ) {
    return "size-col-measure";
  }
  return "size-col-standard";
};

const isNumericVariant = (variant, value) => {
  const columnClass = getVariantColumnClass(variant);
  if (["size-col-quantity", "size-col-price", "size-col-measure"].includes(columnClass)) return true;
  return Number.isFinite(Number(value)) && value !== "";
};

const isInvalidRow = (invalidUnitPrice, product) =>
  invalidUnitPrice?.some((item) => item?.sid === product?.sid);

const isUnitPriceVariant = (variant) =>
  urls?.UNIT_PRICE_CONSTANT?.includes(variant?.variantId);

const isListPriceVariant = (variant) =>
  urls?.LIST_PRICE_CONSTANT?.includes(variant?.variantId);

const hasPriceResponse = (product) => CheckNull(product?.costingToken) !== "";
const hasAnyUserInput = (product, variantsArray) =>
  variantsArray?.some((variant) => {
    if (variant?.sid !== product?.sid) return false;
    if (!variant?.inputByUser) return false;
    const val = CheckNull(variant?.variantValue);
    return val !== "" && val !== null && val !== undefined;
  });

const isRowMissingListPrice = (product, variantsArray) => {
  if (!hasPriceResponse(product)) return false;
  if (!hasAnyUserInput(product, variantsArray)) return false;
  const listPriceVariant = variantsArray?.find(
    (variant) => variant?.sid === product?.sid && isListPriceVariant(variant)
  );
  if (!listPriceVariant) return false;
  const numericValue = Number(CheckNull(listPriceVariant?.variantValue));
  return Number.isNaN(numericValue) || numericValue <= 0;
};

const SizeGridCell = memo(function SizeGridCell({
  variant,
  productIndex,
  rowIndex,
  productConfig,
  values,
  currencyDetail,
  optionsMap,
  configuration = [],
  actuatorLocationOptions = [],
  actuatorLocationLoading = false,
  combinations = [],
  optionsLoading,
  invalidUnitPrice,
  endcaplist,
  onProductChange,
  onProductBlur,
  onKeyDown,
  rowVariants = [],

}) {
  if (isVariantHidden(variant, configuration)) return null;

  const variantValue = getVariantDisplayValue(variant, values, currencyDetail);
  const isSerialNoColumn = variant?.variantName?.trim() === "#";
  const isNumeric = isNumericVariant(variant, variantValue);
  const columnClass = getVariantColumnClass(variant);

  if (urls.END_CAPS.includes(variant?.variantId)) {
    return (
      <td className={`end-caps size-select-cell ${columnClass}`} data-size-editable="true">
        <InlineSelectToggleCell
          value={variant.variantValue}
          placeholder="Select"
          options={
            optionsMap?.[variant?.variantId]?.options?.length > 0
              ? optionsMap[variant.variantId].options.map((option) => ({ label: option, value: option }))
              : endcaplist
          }
          loading={false}
          disabled={!variant?.inputByUser}
          onChange={(event) => onProductChange(event, variant)}
          onBlur={() => onProductBlur(variant, productIndex)}
        />
      </td>
    );
  }

  if (urls?.ACTUATOR_LOCATION?.includes(variant?.variantId)) {
    return (
      <td className={`${columnClass} size-text-cell`} data-size-editable="true">
        <SizeFieldCell
          optionsMap={optionsMap}
          optionsLoading={optionsLoading}
          actuatorLocationOptions={actuatorLocationOptions}
          actuatorLocationLoading={actuatorLocationLoading}
          configuration={configuration}
          varitem={variant}
          productindex={productIndex}
          invalidUnitPrice={invalidUnitPrice}
          HandleProductOnchange={onProductChange}
          HandleProductonfocusout={onProductBlur}
          handleKeyDown={onKeyDown}
          rowVariants={rowVariants}
          productConfig={productConfig}
        />
      </td>
    );
  }

  return (
    <td
      className={`${columnClass} ${isSerialNoColumn ? "size-serial-cell" : isNumeric ? "size-number-cell" : "size-text-cell"}`}
      data-size-editable={variant?.inputByUser === true ? "true" : undefined}
    >
      {isSerialNoColumn ? (
        rowIndex + 1
      ) : variant?.inputByUser === true ? (
        <SizeFieldCell
          optionsMap={optionsMap}
          combinations={combinations}
          optionsLoading={optionsLoading}
          configuration={configuration}
          varitem={variant}
          productindex={productIndex}
          invalidUnitPrice={invalidUnitPrice}
          HandleProductOnchange={onProductChange}
          HandleProductonfocusout={onProductBlur}
          handleKeyDown={onKeyDown}
          rowVariants={rowVariants}
          productConfig={productConfig}
        />
      ) : (
        variantValue || ""
      )}
    </td>
  );
});

const ProductSizeMarginCell = memo(function ProductSizeMarginCell({ product, marginRangeError, onMarginChange, onKeyDown }) {
  const isNegativeMargin = Number(product?.margin) < 0;
  const rowError = marginRangeError?.find((err) => err?.sid === product?.sid);

  return (
    <td
      className={`size-col-margin margin-cell size-number-cell ${isNegativeMargin ? "negative-margin" : ""}`}
      data-size-editable="true"
    >
      <input
        type="text"
        className={`form-control-plaintext px-1 text-right ${rowError ? "is-invalid" : ""}`}
        value={product?.margin || ""}
        placeholder="0"
        onChange={(event) => onMarginChange(event, product)}
        onKeyDown={onKeyDown}
        onFocus={(event) =>
          event.target.addEventListener("wheel", function (e) { e.preventDefault(); }, { passive: false })
        }
      />

      {rowError && (
        <span className="popover__wrapper">
          <div className="popover__content">
            <p className="form-error text-left mt-1">{rowError.message}</p>
          </div>
        </span>
      )}
    </td>
  );
});

const ProductSizeRowActions = memo(function ProductSizeRowActions({
  product,
  actionMenuOpen,
  actionMenuPosition,
  sdrOpen,
  onToggleActionMenu,
  onCloseActionMenu,
  onInsertLineAbove,
  onInsertLineBelow,
  onCopy,
  onDelete,
  onToggleSdr,
  onSdrChange,
  onSdrClear,
}) {
  const isOpen = actionMenuOpen?.show === "show" && actionMenuOpen?.showid === product?.sid;
  const isSdrOpen = sdrOpen?.show === "show" && sdrOpen?.showid === product?.sid;

  return (
    <td className={`action-cell ${isOpen ? "action-cell--open" : ""}`}>
      <div className="dropdown quote-action-dropdown">
        <button
          type="button"
          className="size-row-menu-btn"
          id={`dropdownMenu-${product?.sid}`}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label="Row actions"
          onClick={(event) => onToggleActionMenu(event, product?.sid)}
        >
          <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
        </button>

        <div
          className={`dropdown-menu dropdown-menu-right quote-action-menu${isOpen ? " show" : ""}`}
          aria-labelledby={`dropdownMenu-${product?.sid}`}
          onKeyDown={(event) => {
            if (event.key === "Escape") onCloseActionMenu();
          }}
          style={
            isOpen
              ? {
                position: "fixed",
                top: `${actionMenuPosition.top}px`,
                left: `${actionMenuPosition.left}px`,
                right: "auto",
                bottom: "auto",
              }
              : undefined
          }
        >
          <button className="dropdown-item" type="button" onClick={() => { onInsertLineAbove(product); onCloseActionMenu(); }}>
            Insert line above
          </button>
          <button className="dropdown-item" type="button" onClick={() => { onInsertLineBelow(product); onCloseActionMenu(); }}>
            Insert line below
          </button>
          <button className="dropdown-item" type="button" onClick={() => { onCopy(product); onCloseActionMenu(); }}>
            Copy
          </button>
          <button className="dropdown-item" type="button" onClick={() => { onDelete(product); onCloseActionMenu(); }}>
            Delete
          </button>
          <div className="view-popover-wrapper">
            <button
              type="button"
              className="dropdown-item"
              onClick={() => onToggleSdr(product)}
            >
              SDR Charges
            </button>

            {isSdrOpen && (
              <ProductSdrPopover
                product={product}
                onChange={onSdrChange}
                onClear={onSdrClear}
                onApply={() => onToggleSdr(null)}
              />
            )}
          </div>
        </div>
      </div>
    </td>
  );
});

const ProductSizeNotesCell = memo(function ProductSizeNotesCell({
  product,
  editingId,
  onEditStart,
  onEditEnd,
  onNotesChange,
  onKeyDown,
}) {
  return (
    <td className="notes-cell" data-size-editable={editingId === product.sid ? "true" : undefined}>
      {editingId === product.sid ? (
        <input
          autoFocus
          className="form-control-plaintext px-1"
          value={product?.notes || ""}
          onChange={(event) => onNotesChange(event, product, false)}
          onBlur={onEditEnd}
          onKeyDown={onKeyDown}
        />
      ) : (
        <button
          type="button"
          className={`notes-text${product?.notes ? " has-value" : ""}`}
          onClick={() => onEditStart(product.sid)}
          title={product?.notes}
        >
          {product?.notes || "Enter"}
        </button>
      )}
    </td>
  );
});

const ProductSizeSelectionCell = memo(function ProductSizeSelectionCell({
  product,
  selected,
  onToggle,
}) {
  return (
    <td
      className="size-col-select size-select-cell"
      style={{ width: "48px", minWidth: "48px", textAlign: "center" }}
    >
      <input
        type="checkbox"
        className="product-size-row-checkbox product-size-row-checkbox--row"
        checked={selected}
        onChange={() => onToggle(product?.sid)}
        aria-label={`Select row ${product?.sid}`}
      />
    </td>
  );
});

export const ProductSizeGrid = memo(function ProductSizeGrid({
  productConfigVariant = [],
  addProduct = [],
  variantsArray = [],
  productConfig,
  configuration,
  values,
  currencyDetail,
  optionsMap,
  actuatorLocationOptions = [],
  actuatorLocationLoading = false,
  combinations = [],
  optionsLoading,
  invalidUnitPrice,
  marginRangeError,
  requiredFieldError,
  endcaplist,
  editingId,
  setEditingId,
  actionMenuOpen,
  actionMenuPosition,
  sdrOpen,
  selectedSizeRowSids = [],
  totalQuantity,
  grandTotalNetDisplay,
  onProductChange,
  onProductBlur,
  onKeyDown,
  onNotesChange,
  onToggleActionMenu,
  onCloseActionMenu,
  onInsertLineAbove,
  onInsertLineBelow,
  onCopy,
  onDelete,
  onToggleSizeRowSelection,
  onToggleAllSizeRowSelection,
  onToggleSdr,
  onSdrChange,
  onSdrClear,
  onMarginChange,
  errorMessage,
  isLoading = false,
  visibleColumnKeys,
}) {
  const selectAllCheckboxRef = useRef(null);
  const visibleColumns = productConfigVariant.filter(
    (variant) =>
      !isVariantHidden(variant, productConfig?.configuration) &&
      (!visibleColumnKeys || visibleColumnKeys.includes(variant?.variantId))
  );

  const marginColumnCount = visibleColumns.filter(isUnitPriceVariant).length;
  const totalColSpan = visibleColumns.length + marginColumnCount + 3; // + select + notes + actions
  const selectedCount = selectedSizeRowSids.length;
  const allRowsSelected = addProduct.length > 0 && selectedCount === addProduct.length;
  const someRowsSelected = selectedCount > 0 && !allRowsSelected;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someRowsSelected;
    }
  }, [someRowsSelected]);

  const fewColumnLayout = visibleColumns.length <= 6;
  const denseColumnLayout = visibleColumns.length > 10;
  const gridClassName = [
    "table quote-table product-size-grid",
    fewColumnLayout ? "product-size-grid--few-columns" : "",
    denseColumnLayout ? "product-size-grid--dense-columns" : "",
  ].filter(Boolean).join(" ");

  const footerErrorMessage =
    errorMessage ||
    (marginRangeError?.length > 0 ? marginRangeError[0]?.message : null) ||
    (invalidUnitPrice?.length > 0 ? "Unit price not available for this size. Please recheck size." : null);


  return (
    <div className="quote-table-wrapper product-size-grid-wrapper">
      <style>{`
        .product-size-grid tr.product-size-row--missing-price td {
          border-top: 2px solid #dc3545 !important;
          border-bottom: 2px solid #dc3545 !important;
          background-color: rgba(220, 53, 69, 0.06);
        }
        .product-size-grid tr.product-size-row--missing-price td:first-child {
          border-left: 2px solid #dc3545 !important;
        }
        .product-size-grid tr.product-size-row--missing-price td:last-child {
          border-right: 2px solid #dc3545 !important;
        }
      `}</style>

      <div className="product-size-grid-scroll">
        {isLoading ? (
          <SkeletonProductQuotation />
        ) : (
          <table className={gridClassName}>
            <colgroup>
              <col className="size-col-select" />
              {visibleColumns.map((item) => (
                <React.Fragment key={item?.variantId || item?.variantName}>
                  <col className={getVariantColumnClass(item)} />
                  {isUnitPriceVariant(item) && <col className="size-col-margin" />}
                </React.Fragment>
              ))}
              <col className="size-col-notes" />
              <col className="size-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="size-col-select size-select-header"
                  style={{ width: "48px", minWidth: "48px", textAlign: "center" }}
                >
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    className="product-size-row-checkbox product-size-row-checkbox--select-all"
                    checked={allRowsSelected}
                    onChange={onToggleAllSizeRowSelection}
                    aria-label="Select all rows"
                    disabled={addProduct.length === 0}
                  />
                </th>
                {visibleColumns.map((item) => (
                  <React.Fragment key={item?.variantId || item?.variantName}>
                    <th className={getVariantColumnClass(item)}>
                      {item?.variantName}
                    </th>
                    {isUnitPriceVariant(item) && (
                      <th className="size-col-margin header-margin">Margin(+) / Discount(-)</th>
                    )}
                  </React.Fragment>
                ))}
                <th className="size-col-notes">Notes</th>
                <th className="size-col-actions"></th>
              </tr>
            </thead>

            <tbody>
              {addProduct.length === 0 && (
                <tr className="product-size-empty-row">
                  <td colSpan={totalColSpan}>
                    <div className="product-size-empty-state">
                      <i className="fas fa-ruler-combined" aria-hidden="true"></i>
                      <span>No size lines added yet.</span>
                    </div>
                  </td>
                </tr>
              )}

              {addProduct.map((product, productIndex) => {
                const rowVariantMap = new Map(
                  variantsArray
                    .filter((data) => data.sid === product?.sid)
                    .map((data) => [data.variantId, data])
                );
                const rowVariants = visibleColumns.map((column) => {
                  const rowValue = rowVariantMap.get(column.variantId);
                  return rowValue ? { ...column, ...rowValue } : { ...column, sid: product?.sid, variantValue: "" };
                });
                const invalidRow = isInvalidRow(invalidUnitPrice, product);
                const marginInvalidRow = marginRangeError?.some((err) => err?.sid === product?.sid);
                const requiredInvalidRow = requiredFieldError?.some((err) => err?.sid === product?.sid);
                const isNegativeMargin = Number(product?.margin) < 0;
                const isSelected = selectedSizeRowSids.includes(product?.sid);

                const missingPriceRow = invalidRow || isRowMissingListPrice(product, variantsArray);

                return (
                  <React.Fragment key={product?.sid}>
                    <tr
                      className={[
                        (invalidRow || marginInvalidRow || requiredInvalidRow) ? "product-size-row--invalid" : "",
                        missingPriceRow ? "product-size-row--missing-price" : "",
                        isNegativeMargin ? "negative-margin-row" : "",
                        isSelected ? "product-size-row--selected" : "",
                      ].filter(Boolean).join(" ")}
                      style={isSelected ? { backgroundColor: "#eef5ff" } : undefined}
                      data-size-row-sid={product?.sid}
                    >
                      <ProductSizeSelectionCell
                        product={product}
                        selected={isSelected}
                        onToggle={onToggleSizeRowSelection}
                      />

                      {rowVariants.map((variant) => (
                        <React.Fragment key={`${product?.sid}-${variant?.variantId}`}>
                          <SizeGridCell
                            variant={variant}
                            productIndex={productIndex}
                            rowIndex={productIndex}
                            productConfig={productConfig}
                            configuration={configuration}
                            values={values}
                            currencyDetail={currencyDetail}
                            optionsMap={optionsMap}
                            actuatorLocationOptions={actuatorLocationOptions}
                            actuatorLocationLoading={actuatorLocationLoading}
                            combinations={combinations}
                            optionsLoading={optionsLoading}
                            invalidUnitPrice={invalidUnitPrice}
                            endcaplist={endcaplist}
                            onProductChange={onProductChange}
                            onProductBlur={onProductBlur}
                            onKeyDown={onKeyDown}
                            rowVariants={rowVariants}
                          />
                          {isUnitPriceVariant(variant) && (
                            <ProductSizeMarginCell
                              product={product}
                              marginRangeError={marginRangeError}
                              onMarginChange={onMarginChange}
                              onKeyDown={onKeyDown}
                            />
                          )}
                        </React.Fragment>
                      ))}

                      <ProductSizeNotesCell
                        product={product}
                        editingId={editingId}
                        onEditStart={setEditingId}
                        onEditEnd={() => setEditingId(null)}
                        onNotesChange={onNotesChange}
                        onKeyDown={onKeyDown}
                      />

                      <ProductSizeRowActions
                        product={product}
                        actionMenuOpen={actionMenuOpen}
                        actionMenuPosition={actionMenuPosition}
                        sdrOpen={sdrOpen}
                        onToggleActionMenu={onToggleActionMenu}
                        onCloseActionMenu={onCloseActionMenu}
                        onInsertLineAbove={onInsertLineAbove}
                        onInsertLineBelow={onInsertLineBelow}
                        onCopy={onCopy}
                        onDelete={onDelete}
                        onToggleSdr={(item) =>
                          item
                            ? onToggleSdr({
                              show: sdrOpen?.show === "show" && sdrOpen?.showid === item?.sid ? "hide" : "show",
                              showid: item?.sid,
                            })
                            : onToggleSdr({ show: "hide", showid: product?.sid })
                        }
                        onSdrChange={onSdrChange}
                        onSdrClear={onSdrClear}
                      />
                    </tr>
                    {missingPriceRow && (
                      <tr className="product-size-alert-row">
                        <td className="product-size-row-alert" colSpan={totalColSpan}>
                          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                          Unit price not available for this size. Please recheck size.
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && (
        <div className="table-footer product-size-grid-footer">
          <div className="footer-note">
            <i
              className={`fas ${footerErrorMessage ? "fa-exclamation-triangle" : "fa-info-circle"}`}
              aria-hidden="true"
            ></i>
            <span className={footerErrorMessage ? "form-error" : ""}>
              {footerErrorMessage || "Click on Notes text to edit directly."}
            </span>
          </div>

          <div className="footer-summary">
            <span className="size-summary-item">
              <span>Total Quantity</span>
              <strong>{totalQuantity}</strong>
            </span>

            <span className="size-summary-item">
              <span>Grand Total (Net)</span>
              <strong>{grandTotalNetDisplay}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
